/**
 * Canonical expiring / non-expiring ledger-entry type groups.
 *
 * These are the groups balance accounting uses (src/models/Ledger.js imports
 * them for expiringBalance / nonExpiringBalance), and the admin UI uses the
 * same groups to display an entry's expiration status. This module lives in
 * shared/ (not on LedgerEntry) because the client bundles it and LedgerEntry
 * pulls in prisma. Values mirror LEDGER_ENTRY_TYPES in src/models/LedgerEntry.js;
 * ledgerEntryExpiration.test.ts asserts they stay in sync.
 */
export const EXPIRING_LEDGER_ENTRY_TYPES: readonly string[] = [
  "monthly_allotment",
  "admin_adjustment_expiring",
  "expiring_rollover",
  "word_allotment",
  "grace",
  "benchmark_refund",
];

export const NON_EXPIRING_LEDGER_ENTRY_TYPES: readonly string[] = [
  // If you add new types to this array,
  // include it in the getNonExpiringEntryBreakdowns method (src/models/Ledger.js)
  "pack_allotment",
  "admin_adjustment_nonexpiring",
  "trial",
  "rollover",
];

export type LedgerEntryExpiration = "expiring" | "non_expiring" | "not_applicable";

/**
 * Classifies a ledger entry type for display. Types that don't grant a credit
 * bucket (consumption, cancelled) are "not_applicable" — a yes/no answer would
 * be misleading for them. Note this is independent of context.rolloverEligible:
 * an expiring entry may be rollover-eligible and still eventually expire.
 */
export const classifyLedgerEntryExpiration = (type: string | null | undefined): LedgerEntryExpiration => {
  if (type && EXPIRING_LEDGER_ENTRY_TYPES.includes(type)) return "expiring";
  if (type && NON_EXPIRING_LEDGER_ENTRY_TYPES.includes(type)) return "non_expiring";
  return "not_applicable";
};

const EXPIRATION_LABELS: Record<LedgerEntryExpiration, string> = {
  expiring: "Expiring",
  non_expiring: "Non-expiring",
  not_applicable: "Not applicable",
};

export const ledgerEntryExpirationLabel = (type: string | null | undefined): string => EXPIRATION_LABELS[classifyLedgerEntryExpiration(type)];
